import {
  TaskCategoryTaskType,
  useGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client';
import {
  CampaignActionRequest,
  CampaignActionRequestCampaignAction,
  CampaignDetailModelStage,
  usePerformCampaignAction,
} from '@urbanx/agx.campaigns.client';
import {
  AgxAnimatedToggleLoaderOverlay,
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxYesNoQuestion,
  Icon_AlertCircleOutline,
  Icon_FeaturedIcon,
  useToast,
  YesNo,
} from '@urbanx/agx.ui-components2';
import { useSelectedCampaign } from 'hooks';
import { waitForTaskCollectionRefresh } from 'hooks/useTaskCollectionChanges';
import { useState } from 'react';

const WithdrawListingAction = () => {
  const { campaign } = useSelectedCampaign();
  const { pushToast } = useToast();
  const [hasFundInTrust, setHasFundInTrust] = useState<boolean | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const { data: tasks } = useGetAutopilotTasksForCampaign(
    campaign?.campaignId ?? '',
    undefined,
    !!campaign?.campaignId
  );

  const { performCampaignAction } = usePerformCampaignAction({
    onSuccess: () => {},
    onError: () => {},
  });

  const withdrawnTask = tasks?.find(
    (task) => task.category?.taskType === TaskCategoryTaskType.Withdrawn
  );

  const confirmWithdrawal = async () => {
    const campaignId = campaign?.campaignId;
    if (hasFundInTrust === undefined || !campaignId) return;

    const request: CampaignActionRequest = {
      campaignId,
      campaignAction: CampaignActionRequestCampaignAction.Withdraw,
      hasFundInTrust,
    };

    const taskCollectionRefresh = waitForTaskCollectionRefresh(campaignId);
    setIsWithdrawing(true);

    try {
      await performCampaignAction(request);
      const taskCollectionRefreshResult = await taskCollectionRefresh.promise;

      if (taskCollectionRefreshResult === 'timed-out') {
        pushToast({
          type: 'error',
          message:
            'The listing was withdrawn, but the task refresh could not be confirmed. Refresh the page to check its latest status.',
        });
      }

      setShowConfirmation(false);
    } finally {
      taskCollectionRefresh.cancel();
      setIsWithdrawing(false);
    }
  };

  return (
    <>
      <AgxButton
        id="btnWithdrawListing"
        text="Withdraw Listing"
        variant="naked"
        danger
        onClick={() => setShowConfirmation(true)}
        disabled={
          campaign?.stage === CampaignDetailModelStage.ListingWithdrawn ||
          (withdrawnTask && !withdrawnTask.isLocked)
        }
      />
      <AgxModal
        title="Mark as Withdrawn?"
        titleIcon={
          <>
            {isWithdrawing && (
              <AgxAnimatedToggleLoaderOverlay
                background="transparent"
                label="Withdrawing listing..."
              />
            )}
            <Icon_FeaturedIcon
              icon={<Icon_AlertCircleOutline type="Danger" />}
              color="var(--text-error)"
              backgroundColor="var(--bg-error-light)"
            />
          </>
        }
        type="single"
        variant="withDivider"
        actionButtonContainerStyle="withoutDivider"
        actionButtons={
          <>
            <AgxButton
              id="btnCancel"
              text="Cancel"
              variant="hollow"
              size="large"
              onClick={() => setShowConfirmation(false)}
              stretch
            />
            <AgxButton
              id="btnConfirm"
              text="Confirm"
              variant="primary"
              danger
              size="large"
              onClick={() => void confirmWithdrawal()}
              stretch
              disabled={hasFundInTrust === undefined || isWithdrawing}
            />
          </>
        }
        content={
          <AgxSpacer
            direction="vertical"
            fill
            padding={{ top: 'md', bottom: 'xl', left: 'xl', right: 'xl' }}
          >
            <AgxYesNoQuestion
              id="fundsInTrustQuestion"
              onValueChanged={({ value }: { id: string; value: string }) =>
                setHasFundInTrust(value === YesNo.Yes)
              }
              defaultValue={
                hasFundInTrust === undefined ? undefined : hasFundInTrust ? YesNo.Yes : YesNo.No
              }
              label="Are there any funds in trust?"
              yesLabel="Yes"
              noLabel="No"
              reverseLabelOrder
              required
            />
          </AgxSpacer>
        }
        isOpen={showConfirmation}
        setIsDialogOpen={setShowConfirmation}
      />
    </>
  );
};

export default WithdrawListingAction;
